import { c as _c } from "react/compiler-runtime";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { useAppDispatch } from "#/store/hooks";
import type { SerializedPythonArg } from "../lib/createPythonRuntimeArgs";
import { pythonRunner } from "../lib/pythonRunner";
import { pyodideSlice } from "../model/pyodideSlice";
import type { ExecutionResult } from "./useCodeExecution";
import { usePyodideProgressSnackbar } from "./usePyodideProgressSnackbar";

/** Delay after 100% so the progress bar animation completes before snackbar closes. */
const PROGRESS_COMPLETE_DELAY_MS = 400;
export const usePythonCodeRunner = () => {
  const $ = _c(9);
  const dispatch = useAppDispatch();
  usePyodideProgressSnackbar();
  let t0;
  let t1;
  if ($[0] !== dispatch) {
    t0 = () => {
      if (pythonRunner.isReady) {
        return;
      }
      let cancelled = false;
      dispatch(pyodideSlice.actions.setProgress({
        value: 0,
        stage: "Starting\u2026"
      }));
      let completeTimeoutId = null;
      pythonRunner.init({
        onProgress: (value, stage) => {
          if (!cancelled) {
            dispatch(pyodideSlice.actions.setProgress({
              value,
              stage
            }));
          }
        }
      }).catch(_temp).finally(() => {
        if (cancelled) {
          return;
        }
        completeTimeoutId = setTimeout(() => {
          completeTimeoutId = null;
          if (!cancelled) {
            dispatch(pyodideSlice.actions.clearProgress());
          }
        }, PROGRESS_COMPLETE_DELAY_MS);
      });
      return () => {
        cancelled = true;
        if (completeTimeoutId !== null) {
          clearTimeout(completeTimeoutId);
        }
        dispatch(pyodideSlice.actions.clearProgress());
      };
    };
    t1 = [dispatch];
    $[0] = dispatch;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      mutationFn: _temp2
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    mutateAsync: executePythonCode,
    isPending
  } = useMutation(t2);
  let t3;
  if ($[4] !== executePythonCode) {
    t3 = async (codeInput_0, args_0) => await executePythonCode({
      codeInput: codeInput_0,
      args: args_0
    });
    $[4] = executePythonCode;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const runPythonCode = t3;
  let t4;
  if ($[6] !== isPending || $[7] !== runPythonCode) {
    t4 = {
      runPythonCode,
      isProcessing: isPending
    };
    $[6] = isPending;
    $[7] = runPythonCode;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};
function _temp() {}
async function _temp2(t0) {
  const {
    codeInput,
    args
  } = t0;
  return pythonRunner.run(codeInput, undefined, args);
}